/**
 * AdminRoute Component
 *
 * Route wrapper that requires admin authentication to access.
 * Redirects unauthenticated users or non-admins to admin login while preserving intended destination.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// 2. Internal absolute imports
import { useAuth } from '@/hooks/useAuth';

export interface AdminRouteProps {
  /** Content to render when admin authenticated */
  children: React.ReactNode;
}

/**
 * Admin Route Protection Component
 *
 * Wraps admin routes that require admin authentication. Features:
 * - Supabase Auth-based authentication with role checking
 * - Subdomain restriction (admin.* only)
 * - Session validation with automatic refresh
 * - Redirects non-admins to /admin/login with return URL
 * - Renders children when admin is authenticated
 *
 * Usage:
 * ```tsx
 * <AdminRoute>
 *   <AdminDashboard />
 * </AdminRoute>
 * ```
 */
export function AdminRoute({ children }: AdminRouteProps): React.JSX.Element {
  const location = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [isSubdomainValid, setIsSubdomainValid] = useState<boolean>(true);

  // Check subdomain validity on mount
  useEffect(() => {
    // Enforce admin subdomain access only (allow dev for testing)
    const hostname = window.location.hostname;
    // TODO: Re-enable subdomain check for production
    // Temporarily disabled for local testing on localhost
    if (!hostname.startsWith('admin.') &&
        !hostname.startsWith('dev.') &&
        hostname !== 'localhost' &&
        hostname !== '127.0.0.1') {
      console.log('🚫 AdminRoute: Admin access only allowed on admin.* or dev.* subdomains');
      setIsSubdomainValid(false);
    }
  }, []);

  // Check if user has admin role
  const isAdmin = user?.role === 'admin';

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-default-500">Checking admin authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect if subdomain is invalid
  if (!isSubdomainValid) {
    console.log('🚫 AdminRoute: Invalid subdomain, redirecting to home');
    return <Navigate to="/" replace />;
  }

  // Redirect to admin login if not authenticated, preserving intended destination
  if (!isAuthenticated) {
    console.log('🔒 AdminRoute: Not authenticated, redirecting to admin login from:', location.pathname);
    return (
      <Navigate
        to="/admin/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Redirect if authenticated but not admin
  if (!isAdmin) {
    console.log('🚫 AdminRoute: User is not admin, redirecting to admin login');
    return (
      <Navigate
        to="/admin/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Render admin content when authenticated as admin
  console.log('✅ AdminRoute: Admin authenticated:', user?.email);
  return <>{children}</>;
}
